import { IfPermission } from '@app/core/auth/authorization/components/IfPermission';
import { contentAiPermissions } from '@app/modules/content-ai/content-ai-permissions';
import { SocialPlatformCard } from '@app/modules/social-export/components/common/SocialPlatformCard';
import {
  FacebookPlatform,
  socialPlatforms,
  YoutubePlatform,
} from '@app/modules/social-export/domain/platforms';
import { useToast } from '@app/core/notifications';
import { ContentDto, SocialVersionDto } from '@app/modules/content-ai/content-ai.types';
import { useContentAiPublishSocialVersionContent } from '@mi-front/api-v2';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useContent } from '../../../../hooks/useContent';
import {
  ContentBadge,
  ContentBody,
  ContentTitle,
  OutlinedButton,
  PrimaryButton,
} from '../../components';
import { isSocialOperationInProgress } from './is-social-operation-in-progress';

export function SocialVersionPublish({
  content,
  version,
  onEdit,
  onRegenerate,
}: {
  content: ContentDto;
  version: SocialVersionDto;
  onEdit: () => void;
  onRegenerate: () => void;
}) {
  const queryClient = useQueryClient();
  const { $t } = useIntl();
  const { notifyError } = useToast();
  const query = useContent(content.id);
  const mutation = useContentAiPublishSocialVersionContent();
  const allowActions = !(query.data && isSocialOperationInProgress(query.data));

  const platform = socialPlatforms.get(version.platform);
  const hasTitle =
    version.platform === YoutubePlatform.id || version.platform === FacebookPlatform.id;

  return (
    <SocialPlatformCard platform={platform}>
      <Box>
        {/* Badge */}
        <Box sx={{ mb: 2 }}>
          <ContentBadge
            icon={platform.Icon({
              sx: { width: 16, height: 16, color: '#AF52DE' },
            })}
          >
            <FormattedMessage
              id="contentAi.socialVersion.generatedFor"
              defaultMessage="Contenido generado para {platform}"
              values={{ platform: platform.name }}
            />
          </ContentBadge>
        </Box>

        {/* Title (if applicable) */}
        {hasTitle && <ContentTitle sx={{ mb: 2 }}>{version.userConfig?.title}</ContentTitle>}

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
          <IfPermission permission={contentAiPermissions.Contents_SocialPublish}>
            <PrimaryButton
              disabled={!allowActions}
              onClick={() => {
                mutation.mutate(
                  {
                    id: content.id,
                    platform: version.platform,
                    data: {
                      platform: version.platform,
                      data: {},
                    },
                  },
                  {
                    onSuccess: (response) => {
                      queryClient.setQueryData(query.queryKey, (old: ContentDto | undefined) => {
                        if (!old) {
                          return old;
                        }

                        return {
                          ...old,
                          socialVersions: {
                            versions: [
                              ...old.socialVersions.versions.filter(
                                (v) => v.platform !== version.platform,
                              ),
                              {
                                ...version,
                                uploadId: response.socialExportUploadId,
                              },
                            ],
                          },
                        };
                      });
                      queryClient.invalidateQueries({
                        queryKey: query.queryKey,
                      });
                    },
                    onError: (_error) => {
                      notifyError(
                        $t({
                          id: 'contentAi.socialVersion.publishError',
                          defaultMessage:
                            'Ocurrió un error al publicar la versión, intente nuevamente',
                        }),
                      );
                    },
                  },
                );
              }}
              loading={mutation.isPending}
              sx={{
                fontWeight: 500,
                '&:hover': { backgroundColor: '#9C3FCC' },
              }}
            >
              <FormattedMessage
                id="contentAi.socialVersion.publishContent"
                defaultMessage="Publicar contenido"
              />
            </PrimaryButton>
          </IfPermission>

          <OutlinedButton onClick={onEdit} disabled={!allowActions}>
            <FormattedMessage id="common.edit" defaultMessage="Editar" />
          </OutlinedButton>

          <Box
            sx={{
              height: '28px',
              width: '1px',
              backgroundColor: 'divider',
            }}
          />

          <OutlinedButton onClick={onRegenerate} disabled={!allowActions} startIcon={<CheckIcon />}>
            <FormattedMessage id="contentAi.socialVersion.regenerate" defaultMessage="Regenerar" />
          </OutlinedButton>
        </Box>

        {/* Collapsible Body */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box component="span">
              <FormattedMessage
                id="contentAi.viewFullArticle"
                defaultMessage="Ver artículo completo"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <ContentBody>{version.userConfig?.body}</ContentBody>
          </AccordionDetails>
        </Accordion>
      </Box>
    </SocialPlatformCard>
  );
}
